import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, IconButton, Paper, Box, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link } from 'wouter';
import { AuthContext } from '../contexts/auth';
import { canManageSignedDocs, getCompanySignStats, getSignedDoc } from '../services/api_firme';
import { estraiMessaggiErrore } from '../services/utils';
import Fetching from '../components/fetching';
import CustomTooltipComponent from '../components/custom_tooltip_component';
import FirmeDocsTable from '../components/firme_docs_table';
import NoPermissions from '../authorization/NoPermissions';
import EditDocumentModal from '../components/edit_document_modal';

const isAbortError = err => axios.isCancel(err) || err?.name === 'AbortError' || err?.name === 'CanceledError';

export default function FirmeCompany({ token, selectedEmployee = null }) {
    const { profile } = useContext(AuthContext);
    const allowed = canManageSignedDocs(profile);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [docsById, setDocsById] = useState({});
    const [docsState, setDocsState] = useState({});
    const [editDoc, setEditDoc] = useState(null);

    const statsControllerRef = useRef(null);
    const detailControllerRef = useRef(null);
    const mountedRef = useRef(false);

    /* STATISTICHE */
    const loadStats = useCallback(async (background = false) => {
        if (!token || !allowed) return;
        statsControllerRef.current?.abort();
        const controller = new AbortController();
        statsControllerRef.current = controller;
        if (!background) {
            setLoading(true);
            setError(null);
        }
        try {
            const response = await getCompanySignStats(token, controller.signal);
            if (controller.signal.aborted || !mountedRef.current) return;
            if (statsControllerRef.current !== controller) return;
            setStats(response);
            setError(null);
        } catch (err) {
            if (controller.signal.aborted || isAbortError(err) || !mountedRef.current) return;
            if (statsControllerRef.current !== controller) return;
            if (background) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    title: 'Impossibile aggiornare le statistiche.',
                    timer: 4000,
                    showConfirmButton: false,
                });
            } else {
                setError(estraiMessaggiErrore(err));
            }
        } finally {
            if (statsControllerRef.current === controller && !controller.signal.aborted && mountedRef.current && !background) {
                setLoading(false);
            }

            if (statsControllerRef.current === controller) {
                statsControllerRef.current = null;
            }
        }
    }, [token, allowed]);

    /* CARICAMENTO INIZIALE E CLEANUP */
    useEffect(() => {
        mountedRef.current = true;
        if (allowed) loadStats();
        return () => {
            mountedRef.current = false;
            statsControllerRef.current?.abort();
            detailControllerRef.current?.controller.abort();
            statsControllerRef.current = null;
            detailControllerRef.current = null;
        };
    }, [allowed, loadStats]);

    /* INTERRUZIONE RICHIESTA DETTAGLIO */
    const abortDetail = useCallback(() => {
        const current = detailControllerRef.current;
        if (!current) return;
        current.controller.abort();
        detailControllerRef.current = null;
        if (mountedRef.current) {
            setDocsState(prev => ({
                ...prev,
                [current.documentId]: 'idle',
            }));
        }
    }, []);

    /* CARICAMENTO DETTAGLIO DOCUMENTO */
    const loadDocs = useCallback(async (documentId, force = false) => {
        if (!token || !allowed || !documentId) return;
        abortDetail();
        const controller = new AbortController();
        detailControllerRef.current = { controller, documentId };
        setDocsState(prev => ({
            ...prev,
            [documentId]: 'loading',
        }));
        try {
            const doc = await getSignedDoc(token, documentId, controller.signal);
            if (controller.signal.aborted || !mountedRef.current) return;
            if (detailControllerRef.current?.controller !== controller) return;
            setDocsById(prev => ({ ...prev, [documentId]: doc}));
            setDocsState(prev => ({ ...prev, [documentId]: 'loaded'}));
        } catch (err) {
            if (controller.signal.aborted || isAbortError(err) || !mountedRef.current) return;
            if (detailControllerRef.current?.controller !== controller) return;

            setDocsState(prev => ({ ...prev, [documentId]: 'error'}));
        } finally {
            if (detailControllerRef.current?.controller === controller) {
                detailControllerRef.current = null;
            }
        }
    }, [token, allowed, abortDetail]);

    /* APERTURA E CHIUSURA DOCUMENTO */
    const toggleDocument = useCallback(documentId => {
        if (expandedId === documentId) {
            abortDetail();
            setExpandedId(null);
            return;
        }
        abortDetail();
        setExpandedId(documentId);
        if (!Object.prototype.hasOwnProperty.call(docsById, documentId)) {
            loadDocs(documentId);
        }
    }, [expandedId, docsById, loadDocs, abortDetail]);

    /* DOCUMENTI E STATISTICHE */
    const documents = useMemo(() => stats?.statistiche_documenti || [], [stats]);
    const totals = stats?.statistiche_totali;
    const cards = useMemo(() => [
        { label: 'Totale documenti con firma', value: totals?.total ?? 0, color: 'text-indigo-700', bg: 'bg-indigo-50' },
        { label: 'Documenti firmati', value: totals?.firmati ?? 0, color: 'text-green-700', bg: 'bg-green-50' },
        { label: 'Da firmare', value: totals?.da_firmare ?? 0, color: 'text-amber-700', bg: 'bg-amber-50' },
    ], [totals]);

    /* DIPENDENTE SELEZIONATO DAL COMPONENTE PADRE */
    const selectedEmployeeId = selectedEmployee?.id ?? null;

    /* FILTRO DOCUMENTI PER DIPENDENTE */
    const visibleDocuments = useMemo(() => {
        if (selectedEmployeeId == null) {
            return documents;
        }

        return documents.filter(doc =>
            doc.dipendenti?.some(
                employee => String(employee.id) === String(selectedEmployeeId)
            )
        );
    }, [documents, selectedEmployeeId]);

    /* CHIUDE IL DETTAGLIO SE NON RIENTRA PIÙ NEL FILTRO */
    useEffect(() => {
        if (
            expandedId != null &&
            !visibleDocuments.some(doc => doc.id === expandedId)
        ) {
            abortDetail();
            setExpandedId(null);
        }
    }, [visibleDocuments, expandedId, abortDetail]);

    /* AGGIORNAMENTO DOPO MODIFICHE */
    const handleChanged = useCallback(documentId => {
        loadDocs(documentId, true);
        loadStats(true);
    }, [loadDocs, loadStats]);

    /* MODIFICA DOCUMENTO */
    const handleEditDeadline = doc => {
        const employee = doc.employees?.find(emp =>
            selectedEmployeeId != null &&
            String(emp.id) === String(selectedEmployeeId)
        ) || doc.employees?.[0];

        setEditDoc({
            ...doc,
            employee: employee || null,
        });
    };

    const handleEditSaved = updated => {
        setDocsById(prev => ({
            ...prev,
            [updated.id]: {
                ...(prev[updated.id] || {}),
                ...updated,
            },
        }));

        setEditDoc(null);
        handleChanged(updated.id);
    };

    const handleCloseEdit = () => {
        setEditDoc(null);
    };

    /* PERMESSI */
    if (!allowed) return <NoPermissions />;

    /* LOADING ED ERRORI */
    if (loading) return <Fetching />;
    if (error) {
        return (
            <div className="m-8 flex justify-center rounded-xl bg-red-50 p-10 text-sm text-red-700 shadow-md">
                Errore: {error}
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* CARD STATISTICHE DOCUMENTI CON FIRMA */}
            <div className="mb-6 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">

                {/* INTESTAZIONE */}
                {/* <div className="border-b border-gray-100 px-4 py-4 sm:px-6">
                    <h3 className="text-base font-semibold uppercase text-indigo-800">
                        Statistiche documenti con firma
                    </h3>

                    <p className="mt-1 text-xs text-gray-500">
                        Riepilogo dei documenti caricati, firmati e in attesa di firma
                    </p>
                </div> */}

                {/* CARDS STATISTICHE */}
                <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3 sm:p-6 bg-gray-50">
                    {cards.map(card => (
                        <div key={card.label} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <div className="flex items-start justify-between px-5 py-4">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">{card.label}</dt>
                                </div>
                                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${card.bg}`}>
                                    <span className={`text-xl font-bold ${card.color}`}>{card.value}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

            </div>

            {/* TABELLA PRINCIPALE */}
            <TableContainer component={Paper} className="rounded-lg border border-gray-300 shadow-md !rounded-lg">
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow className="h-12 font-semibold uppercase">
                            <TableCell className="!bg-indigo-50/50" />
                            <TableCell className="!font-semibold !text-indigo-800 !bg-indigo-50/50">Documento</TableCell>
                            <TableCell className="!font-semibold !text-indigo-800 !bg-indigo-50/50" align="left">Firmatari</TableCell>
                            <TableCell className="!font-semibold !text-indigo-800 !bg-indigo-50/50" align="left">Stato</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleDocuments.length ? (
                            visibleDocuments.map(doc => {
                                const expanded = expandedId === doc.id;
                                const detail = docsById[doc.id];
                                const isLoading = docsState[doc.id] === 'loading';
                                const hasError = docsState[doc.id] === 'error';
                                const employees = doc.dipendenti || [];
                                return (
                                    <React.Fragment key={doc.id}>
                                        {/* RIGA DOCUMENTO */}
                                        <TableRow hover className="cursor-pointer" onClick={() => toggleDocument(doc.id)}>
                                            <TableCell width={56} padding="checkbox" onClick={e => e.stopPropagation()}>
                                                <IconButton size="small" onClick={e => { e.stopPropagation(); toggleDocument(doc.id); }}>
                                                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>

                                            <TableCell className="h-14 text-gray-700">
                                                <span className="font-medium">
                                                    {doc.nome}
                                                </span>
                                            </TableCell>

                                            <TableCell className="h-14" align="left">
                                                {employees.length}
                                            </TableCell>

                                            <TableCell className="h-14" align="left">
                                                {doc.is_signed ? (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700">
                                                        Firmato
                                                    </span>
                                                ) : ['ERROR', 'REJECTED'].includes(doc.status) ? (
                                                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700">
                                                        Errore
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">
                                                        Da firmare
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>

                                        {/* DETTAGLIO DOCUMENTO */}
                                        <TableRow className="bg-indigo-50">
                                            <TableCell style={{ padding: 0 }} colSpan={4}>
                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2 }}>

                                                        {/* DIPENDENTI ASSOCIATI */}
                                                        <Typography component="div" className="font-semibold uppercase text-indigo-800" gutterBottom>
                                                            Dipendenti associati al documento
                                                        </Typography>

                                                        <div className="mb-4 flex flex-wrap gap-2">
                                                            {employees.map(employee => {
                                                                const fullName = [
                                                                    employee.cognome,
                                                                    employee.nome,
                                                                ].filter(Boolean).join(' ');

                                                                return (
                                                                    <CustomTooltipComponent key={employee.id} title="Vai all'anagrafica">
                                                                        <Link
                                                                            to={`/dipendenti/${employee.cf}`}
                                                                            className="inline-flex rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-700 hover:text-indigo-700 hover:underline"
                                                                        >
                                                                            {fullName || employee.cf || '—'}
                                                                        </Link>
                                                                    </CustomTooltipComponent>
                                                                );
                                                            })}
                                                        </div>

                                                        {isLoading ? (
                                                            <div className="flex min-h-[100px] w-full items-center justify-center">
                                                                <Fetching />
                                                            </div>
                                                        ) : hasError ? (
                                                            <div className="flex items-center justify-between gap-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
                                                                <span>Impossibile caricare il documento.</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => loadDocs(doc.id, true)}
                                                                    className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                                                                >
                                                                    Riprova
                                                                </button>
                                                            </div>
                                                        ) : detail ? (
                                                            <FirmeDocsTable
                                                                docs={[detail]}
                                                                onEditDeadline={handleEditDeadline}
                                                                onChanged={() => handleChanged(doc.id)}
                                                            />
                                                        ) : null}

                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4 }} className="italic text-gray-500">
                                    {selectedEmployeeId != null
                                        ? 'Nessun documento associato al dipendente selezionato'
                                        : 'Nessun documento da firmare presente'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {editDoc && (
                <EditDocumentModal
                    open={true}
                    doc={editDoc}
                    documentType="signed_document"
                    mode="edit"
                    onClose={handleCloseEdit}
                    onSaved={handleEditSaved}
                    showPreviewModal={false}
                />
            )}
        </div>
    );
}
